import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Alert from 'flarum/common/components/Alert';
import Tooltip from 'flarum/common/components/Tooltip';
import CardComponent from './CardComponent';
import OverrideHistoryModal from './OverrideHistoryModal';
import ElementsGuideModal from './ElementsGuideModal';
import StatsOverviewModal from './StatsOverviewModal';
import CustomCardBackRequestModal from './CustomCardBackRequestModal';
import BattlePassBar from './BattlePassBar';
import QuestsModal from './QuestsModal';
import { BONUS_EFFECT_LABELS, ARENA_CARD_BACKS, transPlain, elementKeys, elementLabel, ARENA_ICONS } from '../../common';
import { questsEnabled } from '../quests';
// Mirrors CreateDeckSlotController::MAX_SLOTS_PER_USER on the backend.
const MAX_DECK_SLOTS = 6;
export default class ArenaProfilePage extends UserPage {
  loading = true;
  savingOverride = false;
  allCards = [];
  deckCardIds = [];
  savedDeckCardIds = [];

  // Autosave status shown by the floating indicator in place of a manual
  // "Сохранить" button (see performSave()/renderAutosaveStatus()) — 'idle'
  // renders nothing, so the indicator only appears once there's actually
  // something to report.
  saveStatus = 'idle';

  // Debounce timer for scheduleAutosave() — coalesces a burst of edits
  // (e.g. building a deck one click at a time) into a single save instead
  // of firing a request per click.
  autosaveTimer = null;

  // Auto-hides the transient "Сохранено" status a bit after a successful
  // save so the indicator doesn't sit there indefinitely once there's
  // nothing left to report.
  savedStatusTimer = null;

  // Every autosave attempt is chained onto this promise (see enqueueSave())
  // instead of just fired directly — since each request sends the *entire*
  // current deckCardIds snapshot rather than a diff, two overlapping
  // requests finishing out of order could let a stale one overwrite a
  // newer save. Chaining forces them to run one at a time, always reading
  // deckCardIds at the moment each actually starts.
  saveChain = (() => Promise.resolve())();

  // Drag-to-reorder within the deck halves below (see swapCards()) — id of
  // the card currently being dragged, or null when no drag is in progress.
  draggingCardId = null;

  // Safety net against a core/theme layout bug confirmed via the site's own
  // DevTools: .Page-container (core's flex row wrapping the sidebar nav +
  // this page's content) won't shrink below this row's un-shrunk card
  // content, so instead of trusting any ancestor's resolved width — exactly
  // what's unreliable — each row measures its own position against the
  // nearest .Page-container and caps its own width from that (see
  // clampDeckRowWidth()). DOM refs kept here so a window resize can
  // re-clamp both rows without a full redraw.
  elementalDeckRowEl = null;
  bonusDeckRowEl = null;

  // Chevron nav (see renderDeckRow()) shows only once a row actually
  // overflows — same pattern as BattlePassBar's canScroll/measureOverflow().
  elementalDeckRowCanScroll = false;
  bonusDeckRowCanScroll = false;
  deckSlots = [];
  selectedDeckSlotId = null;
  loadingDeckCards = false;
  slotActionLoading = false;
  showNewSlotInput = false;
  newSlotName = '';
  renamingSlotId = null;
  renameSlotName = '';
  filterElementalElement = 'all';
  filterElementalCost = '';
  filterElementalSearch = '';
  filterBonusCost = '';
  filterBonusEffect = 'all';
  filterBonusSearch = '';
  hideElementalAdded = false;
  hideBonusAdded = false;
  overrideHp = '';
  overrideMana = '';
  savingCardBack = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.loadUser(m.route.param('username'));
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    // Two separate guards for two separate ways of "leaving the Arena tab":
    // 1. beforeunload — closing/reloading the tab or typing a new URL.
    //    Browsers ignore any custom returnValue text here, so this one
    //    intentionally carries none.
    // 2. A capture-phase click listener — switching to a different in-app
    //    route, which never fires beforeunload. Registered with `true` for
    //    the capture phase so it can cancel the click before Mithril's own
    //    router click handler sees it. This one can show real custom text
    //    via confirm().
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    document.addEventListener('click', this.handleLeaveClick, true);
    window.addEventListener('resize', this.onDeckRowResize);
  }
  onremove(vnode) {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('click', this.handleLeaveClick, true);
    window.removeEventListener('resize', this.onDeckRowResize);
    // Route changes that don't go through an in-app <a> click (back/forward,
    // a manually-called m.route.set() elsewhere) skip handleLeaveClick
    // entirely — flush unconditionally here too so a still-debounced edit
    // doesn't just get dropped. The request outlives this component either
    // way (fetch doesn't care that the vnode's gone).
    if (this.isDeckDirty()) {
      this.flushAutosave();
    }
    if (this.autosaveTimer != null) {
      window.clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.savedStatusTimer != null) {
      window.clearTimeout(this.savedStatusTimer);
      this.savedStatusTimer = null;
    }
    super.onremove(vnode);
  }

  // The row sits between two chevron nav buttons inside
  // .ArenaProfilePage-deckRowSlider (28px each + the slider's 4px gap on
  // each side — see forum.less). Both are reserved unconditionally, even
  // while currently hidden (canScroll false): the *prev* button is what's
  // hidden most of the time, and it sits to the row's own LEFT — so the
  // instant a row grows wide enough to need chevrons, prev popping into
  // view shifts the row's left edge rightward by this same amount, one
  // render after the width was computed from its old (unshifted) position.
  // Confirmed via Playwright: reserving only the trailing chevron's width
  // still left a steady overflow exactly equal to the leading one's width
  // the moment both appeared together. Measuring from the SLIDER wrapper
  // instead of the row — see clampDeckRowWidth below — sidesteps this
  // entirely, since the slider's own left edge doesn't move when its
  // children's visibility toggles.
  static DECK_ROW_NAV_RESERVED_PX = 64; // 2 × (28px button + 4px gap)

  /**
   * Caps a deck row's own rendered width directly off the nearest
   * .Page-container instead of trusting whatever width an ancestor resolves
   * it to. Works around a core/theme layout bug (confirmed via the site's
   * own DevTools — .Page-container is a flex row wrapping the sidebar nav +
   * this page's content) where that flex row refuses to shrink below this
   * row's un-shrunk card content and blows the whole page out sideways
   * instead — by the time that happens, the row's OWN width is exactly
   * what's wrong, so measuring it (or anything sized relative to it) can't
   * be trusted. Clamping against .Page-container's own right edge — rather
   * than the raw viewport edge — also respects its centering/margins on
   * wide screens, instead of letting the row bleed into that empty space.
   * Falls back to the viewport if no .Page-container is found (a different
   * core/theme layout), so this still degrades safely either way.
   *
   * Measured from the row's own .ArenaProfilePage-deckRowSlider wrapper,
   * not the row itself: the row's left edge shifts by a chevron's width
   * the moment that chevron's visibility toggles (see
   * DECK_ROW_NAV_RESERVED_PX above), but the slider wrapper's left edge —
   * anchored to its own parent, not to which of its children are
   * display:none — never does. Falls back to the row itself if the
   * wrapper isn't found for some reason, so this still degrades safely.
   */
  clampDeckRowWidth(el) {
    const slider = el.closest('.ArenaProfilePage-deckRowSlider');
    const anchorRect = (slider ?? el).getBoundingClientRect();
    const container = el.closest('.Page-container');
    const rightEdge = container ? container.getBoundingClientRect().right : window.innerWidth;
    const maxWidth = Math.max(200, rightEdge - anchorRect.left - ArenaProfilePage.DECK_ROW_NAV_RESERVED_PX - 4);
    el.style.maxWidth = `${maxWidth}px`;
  }

  /** Shows/hides a row's chevron nav — mirrors BattlePassBar's measureOverflow(). */
  measureDeckRowOverflow(el, rowKey) {
    const canScroll = el.scrollWidth > el.clientWidth + 4;
    const prop = rowKey === 'elemental' ? 'elementalDeckRowCanScroll' : 'bonusDeckRowCanScroll';
    if (this[prop] !== canScroll) {
      this[prop] = canScroll;
      m.redraw();
    }
  }
  scrollDeckRowByPage(rowKey, direction) {
    const el = rowKey === 'elemental' ? this.elementalDeckRowEl : this.bonusDeckRowEl;
    if (!el) return;
    // Clamp the target instead of a plain scrollBy(): scrollBy's delta isn't
    // bounded to what's actually left to scroll, so pressing an end-side
    // chevron once already at that end commands a scroll past the real max
    // — mobile Safari/Chrome then rubber-bands it back, visibly scrolling
    // into empty space before springing back. Computing the clamped target
    // ourselves means we never ask for more scroll than exists, so there's
    // nothing to spring back from.
    const delta = direction * Math.max(160, el.clientWidth * 0.7);
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const target = Math.min(Math.max(el.scrollLeft + delta, 0), maxScrollLeft);
    el.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  }
  onDeckRowResize = () => {
    if (this.elementalDeckRowEl) {
      this.clampDeckRowWidth(this.elementalDeckRowEl);
      this.measureDeckRowOverflow(this.elementalDeckRowEl, 'elemental');
    }
    if (this.bonusDeckRowEl) {
      this.clampDeckRowWidth(this.bonusDeckRowEl);
      this.measureDeckRowOverflow(this.bonusDeckRowEl, 'bonus');
    }
  };
  handleBeforeUnload = e => {
    if (!this.isDeckDirty()) return;
    e.preventDefault();
    e.returnValue = '';
  };

  // A merely-pending autosave (debounce not fired yet, or a request still
  // in flight) doesn't block this click — the underlying fetch survives an
  // in-app route change and completes in the background regardless, so
  // this just makes sure it's actually fired rather than sitting debounced
  // (see flushAutosave()). Only a save already known to have failed
  // (saveStatus === 'error') gets the confirm() prompt — that's the one
  // case autosave genuinely can't recover from on its own.
  handleLeaveClick = e => {
    if (!this.isDeckDirty() && this.saveStatus !== 'error') return;
    const anchor = e.target?.closest?.('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;
    if (anchor.getAttribute('target') === '_blank') return; // opens in a new tab, nothing lost here
    if (href === m.route.get()) return; // same route (e.g. re-clicking the current tab)

    if (this.saveStatus === 'error' && !confirm(transPlain('forumaker-arena.forum.profile.deck.leave_tab_confirm'))) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    this.flushAutosave();
  };
  show(user) {
    super.show(user);
    const overrides = user.attribute('arenaUserOverrides');
    this.overrideHp = overrides?.maxHp != null ? String(overrides.maxHp) : '';
    this.overrideMana = overrides?.maxMana != null ? String(overrides.maxMana) : '';
    this.loadData(user);
  }
  async loadData(user) {
    this.loading = true;
    m.redraw();
    try {
      // CardResource's JSON:API index, sorted by `order` server-side.
      const cards = await app.store.find('arena-cards', {
        page: {
          limit: 500
        }
      });
      let allCards = (Array.isArray(cards) ? cards : cards ? [cards] : []).filter(c => !!c);

      // An actor with arena.manageCards gets every card back from
      // CardResource unfiltered, including personal cards nobody's granted
      // (this profile's user, or anyone else), since the same endpoint
      // serves the admin management list too. Without this filter, such an
      // actor's deck-building grid showed those cards as pickable, but
      // saving a deck with one always failed server-side — this ran for the
      // profile owner viewing their own page, but NOT for an admin viewing
      // someone else's (arenaGrantedCardIds used to come back null there,
      // so the filter silently no-opped and every personal card in the
      // system showed up as selectable). arenaGrantedCardIds is now visible
      // to arena.manageCards actors for any profile, not just their own —
      // see its own backend comment — so this same filter now covers both.
      if (this.isProfileOwner() || this.canManageCards()) {
        const grantedCardIds = new Set(user.attribute('arenaGrantedCardIds') ?? []);
        allCards = allCards.filter(c => c.isPublic() !== false || grantedCardIds.has(parseInt(c.id())));
      }
      this.allCards = allCards;
      if (this.canManageDeck()) {
        await this.loadDeckSlots(user);
      }
    } catch (e) {
      console.error('[Arena] Failed to load profile cards/decks:', e);
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('forumaker-arena.forum.profile.load_error'));
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  // Order-sensitive (no .sort() before comparing): card order is now
  // meaningful — it's what SaveDeckController persists as each row's
  // `position`, and addCardSlider.tsx displays the in-battle slider in that
  // same saved order (see its deckCards mapping). A pure reorder via
  // swapCards() below (same ids, different arrangement) must still flip
  // this dirty so performSave() knows there's something to autosave.
  isDeckDirty() {
    return JSON.stringify(this.deckCardIds) !== JSON.stringify(this.savedDeckCardIds);
  }
  async loadDeckSlots(user) {
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots`,
        params: {
          userId: user.id()
        }
      });
      this.deckSlots = response?.slots ?? [];
    } catch {
      this.deckSlots = [];
    }

    // Every player needs at least one deck slot to build a deck — create the
    // first one automatically for the profile owner (nobody else has the
    // permission, and auto-creating for a viewer wouldn't make sense).
    if (this.deckSlots.length === 0 && this.isProfileOwner()) {
      try {
        const created = await app.request({
          method: 'POST',
          url: `${app.forum.attribute('apiUrl')}/arena/deck-slots`,
          body: {
            name: transPlain('forumaker-arena.forum.profile.deck.default_slot_name')
          }
        });
        this.deckSlots = [created];
      } catch {
        // fail silently — the user can still create a slot manually below
      }
    }
    this.selectedDeckSlotId = this.deckSlots[0]?.id ?? null;
    if (this.selectedDeckSlotId != null) {
      await this.loadSlotCards(this.selectedDeckSlotId);
    }
  }
  async loadSlotCards(slotId) {
    this.loadingDeckCards = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots/${slotId}/cards`
      });
      const ids = response?.cardIds ?? [];
      this.deckCardIds = [...ids];
      this.savedDeckCardIds = [...ids];
    } catch {
      this.deckCardIds = [];
      this.savedDeckCardIds = [];
    } finally {
      this.loadingDeckCards = false;
      m.redraw();
    }
  }
  async selectSlot(slotId) {
    if (slotId === this.selectedDeckSlotId || this.loadingDeckCards) return;
    // Flush first — the common case is a debounced or in-flight autosave
    // that just needs to actually land, which needs no confirmation at
    // all. Only if it's *still* dirty afterwards (the save itself failed)
    // do we fall back to asking before discarding it.
    if (this.isDeckDirty()) {
      await this.flushAutosave();
      if (this.isDeckDirty() && !confirm(transPlain('forumaker-arena.forum.profile.deck.discard_confirm'))) {
        return;
      }
    }
    this.selectedDeckSlotId = slotId;
    await this.loadSlotCards(slotId);
  }
  async createSlot() {
    const name = this.newSlotName.trim();
    if (!name || this.slotActionLoading) return;
    this.slotActionLoading = true;
    m.redraw();
    try {
      const created = await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots`,
        body: {
          name
        },
        errorHandler: () => {} // see saveDeck() above
      });
      this.deckSlots = [...this.deckSlots, created];
      this.newSlotName = '';
      this.showNewSlotInput = false;
      this.selectedDeckSlotId = created.id;
      this.deckCardIds = [];
      this.savedDeckCardIds = [];
    } catch (e) {
      const body = e?.response ?? {};
      app.alerts.show({
        type: 'error'
      }, body?.error ?? app.translator.trans('forumaker-arena.forum.profile.deck.slot_error'));
    } finally {
      this.slotActionLoading = false;
      m.redraw();
    }
  }
  startRename(slot) {
    this.renamingSlotId = slot.id;
    this.renameSlotName = slot.name;
  }
  cancelRename() {
    this.renamingSlotId = null;
  }
  async confirmRename() {
    if (this.renamingSlotId == null || this.slotActionLoading) return;
    const name = this.renameSlotName.trim();
    if (!name) return;
    const slotId = this.renamingSlotId;
    this.slotActionLoading = true;
    m.redraw();
    try {
      await app.request({
        method: 'PATCH',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots/${slotId}`,
        body: {
          name
        },
        errorHandler: () => {} // see saveDeck() above
      });
      this.deckSlots = this.deckSlots.map(s => s.id === slotId ? {
        ...s,
        name
      } : s);
      this.renamingSlotId = null;
    } catch (e) {
      const body = e?.response ?? {};
      app.alerts.show({
        type: 'error'
      }, body?.error ?? app.translator.trans('forumaker-arena.forum.profile.deck.slot_error'));
    } finally {
      this.slotActionLoading = false;
      m.redraw();
    }
  }
  async deleteSlot(slot) {
    if (this.slotActionLoading) return;
    if (!confirm(transPlain('forumaker-arena.forum.profile.deck.delete_confirm', {
      name: slot.name
    }))) {
      return;
    }
    this.slotActionLoading = true;
    m.redraw();
    try {
      await app.request({
        method: 'DELETE',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots/${slot.id}`,
        errorHandler: () => {} // see saveDeck() above
      });
      this.deckSlots = this.deckSlots.filter(s => s.id !== slot.id);
      if (this.selectedDeckSlotId === slot.id) {
        this.selectedDeckSlotId = this.deckSlots[0]?.id ?? null;
        if (this.selectedDeckSlotId != null) {
          await this.loadSlotCards(this.selectedDeckSlotId);
        } else {
          this.deckCardIds = [];
          this.savedDeckCardIds = [];
        }
      }
    } catch (e) {
      const body = e?.response ?? {};
      app.alerts.show({
        type: 'error'
      }, body?.error ?? app.translator.trans('forumaker-arena.forum.profile.deck.slot_error'));
    } finally {
      this.slotActionLoading = false;
      m.redraw();
    }
  }
  isProfileOwner() {
    const sid = app.session.user?.id();
    const uid = this.user?.id();
    return !!sid && !!uid && sid === uid;
  }
  isAdmin() {
    return !!app.session.user?.attribute('isAdmin');
  }
  canManageOverrides() {
    const u = app.session.user;
    if (!u) return false;
    return u.attribute('isAdmin') || app.forum.attribute('canManageOverrides');
  }
  canViewDecks() {
    return !!app.forum.attribute('canViewDecks');
  }

  // Same permission CardResource::scope()'s admin bypass is keyed to — not
  // isAdmin(), which is a broader Flarum flag a non-admin moderator granted
  // just this one permission wouldn't have. See loadData()'s
  // arenaGrantedCardIds filtering, which needs exactly this distinction.
  canManageCards() {
    return !!app.forum.attribute('canManageCards');
  }
  canManageDeck() {
    return this.isProfileOwner() || this.isAdmin() || this.canViewDecks();
  }
  hasActiveBattle() {
    // arenaActiveBattles is a list (a user can have several concurrent
    // battles against different opponents) — an empty array is truthy in
    // JS, so length must be checked explicitly rather than just `!!`.
    const battles = this.user?.attribute('arenaActiveBattles');
    return !!battles && battles.length > 0;
  }

  // Deck slot ids currently locked in by one of this user's own active
  // battles (see ArenaDeckUserFields::arenaActiveBattles — each entry now
  // carries the specific slot that battle used, via ArenaBattle::deckSlotIdFor()).
  // A player can own several decks and only the one actually in a fight is
  // locked — the others remain freely editable.
  //
  // Only counts entries where deckConfirmed is true. A freshly-challenged
  // opponent gets a deck slot auto-picked for them by CreateBattleController
  // before they've entered the battle or chosen anything themselves (see
  // ArenaBattle::isDeckConfirmedFor()'s docblock) — showing that slot as
  // "locked" this early would tell a player who hasn't even seen the
  // challenge yet that their deck is unavailable, which isn't true: it stays
  // freely editable until they actually confirm/enter the fight (or the
  // challenger, whose pick is always confirmed already).
  lockedDeckSlotIds() {
    const battles = this.user?.attribute('arenaActiveBattles');
    const ids = (battles ?? []).filter(b => b?.deckConfirmed).map(b => b?.deckSlotId).filter(id => id != null);
    return new Set(ids);
  }
  isSlotLocked(slotId) {
    return slotId != null && this.lockedDeckSlotIds().has(slotId);
  }
  toggleCard(card) {
    // isSlotLocked() no longer blocks editing here — every active battle
    // now plays against its own frozen deck snapshot (challenger_deck_
    // override/opponent_deck_override, set at deck-lock time — see
    // ArenaBattle::deckCardIdsFor()'s docblock), so changing this slot
    // can't affect it. isSlotLocked() is still used below purely to show
    // the "won't affect your current battle" heads-up.
    if (this.selectedDeckSlotId == null) return;
    const id = parseInt(card.id());
    const isBonus = (card.cardType?.() ?? 'elemental') === 'bonus';
    if (this.deckCardIds.includes(id)) {
      this.deckCardIds = this.deckCardIds.filter(x => x !== id);
    } else {
      const countOfType = this.deckCardIds.filter(did => {
        const dc = this.allCards.find(c => parseInt(c.id()) === did);
        return dc && (dc.cardType?.() ?? 'elemental') === 'bonus' === isBonus;
      }).length;
      if (countOfType < 10) {
        this.deckCardIds = [...this.deckCardIds, id];
      }
    }
    this.scheduleAutosave();
    m.redraw();
  }

  /**
   * Drag-to-reorder within a deck half (see the elemental/bonus card rows
   * below) — swaps two cards' positions in the underlying flat
   * deckCardIds array. Deliberately swaps raw array slots rather than
   * splicing within a per-type subset: deckElementalIds/deckBonusIds are
   * just deckCardIds filtered by type, so swapping the two matching slots
   * in the full array reorders both the flat array and each type's
   * filtered view consistently, regardless of what other-type ids sit
   * between them. Only ever called with two ids of the same half — the
   * drop handler below checks that before calling this.
   */
  swapCards(idA, idB) {
    if (idA === idB) return;
    const ids = [...this.deckCardIds];
    const iA = ids.indexOf(idA);
    const iB = ids.indexOf(idB);
    if (iA === -1 || iB === -1) return;
    [ids[iA], ids[iB]] = [ids[iB], ids[iA]];
    this.deckCardIds = ids;
    this.scheduleAutosave();
    m.redraw();
  }

  /**
   * One deck half (elemental or bonus) as a horizontal, drag-to-reorder
   * row — same .ArenaCardSlider-cards row styling and compact card size as
   * the in-battle card picker (see CardSlider.tsx's own -cards row), so the
   * deck editor previews how this same set will actually lay out during a
   * battle. `ids` is scoped to one half already (deckElementalIds or
   * deckBonusIds), which is also what keeps drag-and-drop from crossing
   * halves: ondrop only calls swapCards() when the dragged id is found
   * within THIS row's own `ids`, so dropping an elemental card onto the
   * bonus row (or vice versa) is a no-op.
   *
   * Navigated with chevron buttons flanking the row — same slider pattern
   * as BattlePassBar (scrollByPage/measureOverflow/canScroll), chosen
   * specifically because every card here is itself draggable (for
   * reordering): a mouse-drag on the row reorders a card instead of
   * scrolling it, unlike the in-battle slider (no draggable cards there),
   * which scrolls by letting a mouse user grab-and-drag the row directly.
   */
  renderDeckRow(ids, deckDisabled, cardBackImage, rowKey) {
    const storeRef = dom => {
      const el = dom;
      if (rowKey === 'elemental') this.elementalDeckRowEl = el;else this.bonusDeckRowEl = el;
      this.clampDeckRowWidth(el);
      this.measureDeckRowOverflow(el, rowKey);
    };
    const canScroll = rowKey === 'elemental' ? this.elementalDeckRowCanScroll : this.bonusDeckRowCanScroll;
    return m("div", {
      className: `ArenaProfilePage-deckRowSlider ${canScroll ? 'ArenaProfilePage-deckRowSlider--scrollable' : ''}`
    }, m(Button, {
      className: "Button Button--icon ArenaProfilePage-deckRowNav ArenaProfilePage-deckRowNav--prev",
      icon: "fas fa-chevron-left",
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.scroll_back'),
      onclick: () => this.scrollDeckRowByPage(rowKey, -1)
    }), m("div", {
      className: "ArenaCardSlider-cards ArenaCardGrid--inDeck ArenaProfilePage-deckRow"
      // See clampDeckRowWidth()'s docblock — caps this row's own width
      // off the nearest .Page-container as a safety net, independent of
      // whatever an ancestor's layout resolves it to.
      ,
      oncreate: vnode => storeRef(vnode.dom),
      onupdate: vnode => storeRef(vnode.dom)
    }, ids.map(id => {
      const card = this.allCards.find(c => parseInt(c.id()) === id);
      if (!card) return null;
      return m("div", {
        key: id,
        className: `ArenaProfilePage-deckRowItem ${this.draggingCardId === id ? 'is-dragging' : ''}`,
        draggable: !deckDisabled,
        ondragstart: e => {
          this.draggingCardId = id;
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        },
        ondragover: e => {
          // Required so the browser allows a drop here at all — see MDN's
          // drag-and-drop docs (dragover's default action is "no drop").
          if (this.draggingCardId != null) e.preventDefault();
        },
        ondrop: e => {
          e.preventDefault();
          if (this.draggingCardId != null && ids.includes(this.draggingCardId)) {
            this.swapCards(this.draggingCardId, id);
          }
          this.draggingCardId = null;
        },
        ondragend: () => {
          this.draggingCardId = null;
          m.redraw();
        }
      }, m(CardComponent, {
        card: card,
        compact: true,
        selected: true,
        disabled: deckDisabled,
        cardBackImage: cardBackImage,
        onclick: () => this.toggleCard(card)
      }));
    })), m(Button, {
      className: "Button Button--icon ArenaProfilePage-deckRowNav ArenaProfilePage-deckRowNav--next",
      icon: "fas fa-chevron-right",
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.scroll_forward'),
      onclick: () => this.scrollDeckRowByPage(rowKey, 1)
    }));
  }

  // Debounced entry point — toggleCard()/swapCards() call this after every
  // edit instead of waiting for a manual save. Resets on each call so a
  // burst of clicks (building a deck one card at a time) collapses into one
  // request instead of one per click.
  scheduleAutosave() {
    if (this.savedStatusTimer != null) {
      window.clearTimeout(this.savedStatusTimer);
      this.savedStatusTimer = null;
    }
    if (this.autosaveTimer != null) {
      window.clearTimeout(this.autosaveTimer);
    }
    // Clears a lingering "Сохранено"/error from a previous edit right away
    // instead of leaving it on screen — stale but technically-still-true —
    // for the whole debounce window. Nothing to show until the request
    // actually starts (performSave() sets 'saving' itself).
    this.saveStatus = 'idle';
    this.autosaveTimer = window.setTimeout(() => {
      this.autosaveTimer = null;
      this.enqueueSave();
    }, 700);
  }

  // Appends one performSave() call onto the shared chain — see saveChain's
  // own comment for why this matters instead of just calling performSave()
  // directly.
  enqueueSave() {
    this.saveChain = this.saveChain.then(() => this.performSave());
  }

  // Cancels any pending debounce and runs (or waits out) a save right now —
  // used before switching deck slots and when leaving the page, so neither
  // has to race a still-debounced edit. Resolves once the chain is
  // genuinely idle; check isDeckDirty()/saveStatus afterwards to see
  // whether it actually succeeded.
  async flushAutosave() {
    if (this.autosaveTimer != null) {
      window.clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
      this.enqueueSave();
    }
    await this.saveChain;
  }
  async performSave() {
    if (!this.isDeckDirty() || this.selectedDeckSlotId == null || this.deckCardIds.length > 20) return;
    const slotId = this.selectedDeckSlotId;
    const snapshot = [...this.deckCardIds];
    this.saveStatus = 'saving';
    m.redraw();
    try {
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/deck`,
        body: {
          cardIds: snapshot,
          deckSlotId: slotId
        },
        // Suppress Flarum's own global error alert (it can't render our
        // custom JSON error shape and shows an empty box) — the status
        // indicator already reports failure.
        errorHandler: () => {}
      });
      // The player may have switched to a different slot while this request
      // was in flight (selectSlot() awaits flushAutosave() first, but a
      // save already mid-flight when they clicked can't be un-sent) —
      // applying a stale slot's result to the new slot's state would
      // corrupt it, so only commit if we're still looking at the same slot.
      if (this.selectedDeckSlotId === slotId) {
        this.savedDeckCardIds = snapshot;
        this.deckSlots = this.deckSlots.map(s => s.id === slotId ? {
          ...s,
          cardCount: snapshot.length
        } : s);
        if (this.isDeckDirty()) {
          // More edits landed while this request was in flight — leave the
          // indicator on "saving" rather than flashing "saved"; the edit
          // that made it dirty again already called scheduleAutosave().
          this.saveStatus = 'saving';
        } else {
          this.saveStatus = 'saved';
          this.savedStatusTimer = window.setTimeout(() => {
            this.savedStatusTimer = null;
            this.saveStatus = 'idle';
            m.redraw();
          }, 1500);
        }
      }
    } catch {
      if (this.selectedDeckSlotId === slotId) {
        this.saveStatus = 'error';
      }
    } finally {
      m.redraw();
    }
  }

  // Alert's own dismiss (×) button — just hides the indicator early. Doesn't
  // touch deckCardIds/savedDeckCardIds, so dismissing an 'error' alert
  // doesn't pretend the save succeeded: the deck stays dirty and the next
  // edit (or a manual retry before dismissing) will try again.
  dismissAutosaveStatus() {
    if (this.savedStatusTimer != null) {
      window.clearTimeout(this.savedStatusTimer);
      this.savedStatusTimer = null;
    }
    this.saveStatus = 'idle';
    m.redraw();
  }
  async saveOverride() {
    const userId = this.user?.id();
    if (!userId) return;
    this.savingOverride = true;
    m.redraw();
    try {
      const body = {};
      if (this.overrideHp !== '') body.maxHp = parseInt(this.overrideHp) || null;
      if (this.overrideMana !== '') body.maxMana = parseInt(this.overrideMana) || null;
      if (this.overrideHp === '') body.maxHp = null;
      if (this.overrideMana === '') body.maxMana = null;
      await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/users/${userId}/overrides`,
        body,
        errorHandler: () => {} // see saveDeck() above
      });
      await app.store.find('users', userId);
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('forumaker-arena.forum.profile.override.saved'));
    } catch {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('forumaker-arena.forum.profile.override.error'));
    } finally {
      this.savingOverride = false;
      m.redraw();
    }
  }
  async selectCardBack(id) {
    if (!this.isProfileOwner() || this.savingCardBack) return;
    const user = this.user;
    if (user.attribute('arenaCardBack') === id) return;
    this.savingCardBack = true;
    m.redraw();
    try {
      await user.savePreferences({
        arenaCardBack: id
      });
      m.redraw();
    } catch {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('forumaker-arena.forum.profile.card_backs.error'));
    } finally {
      this.savingCardBack = false;
      m.redraw();
    }
  }

  /**
   * The "Кастомная" tile's click handler — not a selectCardBack() variant
   * since it doesn't equip anything, it opens a request modal. A pending
   * request short-circuits straight to the "already sent" alert instead of
   * reopening the modal, per the profile-owner-only guard mirrored from
   * selectCardBack() above.
   */
  openCustomCardBackRequest() {
    const user = this.user;
    if (user.attribute('arenaHasPendingCustomCardBackRequest')) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('forumaker-arena.forum.profile.card_backs.custom.already_pending'));
      return;
    }
    app.modal.show(CustomCardBackRequestModal, {
      user
    });
  }
  content() {
    if (this.loading) return m(LoadingIndicator, null);
    const user = this.user;
    const stats = user.attribute('arenaStats') ?? {
      wins: 0,
      losses: 0,
      draws: 0
    };
    const currentStats = user.attribute('arenaCurrentStats');
    const manaLabel = app.translator.trans('forumaker-arena.lib.stats.mana');
    const searchLabel = app.translator.trans('forumaker-arena.forum.profile.filter.search_placeholder');
    // This profile's equipped card back, applied to their deck/pool cards
    // below — rendered for every viewer of this page, not just the owner,
    // so it reads arenaCardBackDetails (visible to all, resolved
    // server-side) rather than resolving the raw arenaCardBack id against
    // the client-side ARENA_CARD_BACKS list, which no longer carries
    // private ("Личная") backs at all (see FilterActiveCardBacks).
    const cardBackImage = user.attribute('arenaCardBackDetails')?.imageUrl ?? null;
    const isProfileOwner = this.isProfileOwner();
    const canManage = this.canManageDeck();
    // No longer gates editing (see toggleCard()'s comment) — kept only to
    // show the ArenaProfilePage-battleLock heads-up below.
    const slotLocked = isProfileOwner && this.isSlotLocked(this.selectedDeckSlotId);
    const deckDisabled = this.selectedDeckSlotId == null || this.loadingDeckCards;
    const deckSet = new Set(this.deckCardIds);
    // Total mana cost to play every card currently in the loaded deck slot
    // (elemental + bonus) — shown next to the "20 из 20" count badge so the
    // player can eyeball how mana-hungry their deck is at a glance.
    const deckManaCost = this.deckCardIds.reduce((sum, id) => {
      const card = this.allCards.find(c => parseInt(c.id()) === id);
      return sum + (card?.cost?.() ?? 0);
    }, 0);
    // currentStats.maxMana already resolves this specific user's actual pool
    // (per-user override if set, otherwise the global setting — see
    // ArenaBattle::getMaxHpForUser()/getMaxManaForUser() on the backend), so
    // this comparison is correct regardless of whether their mana is the
    // default 24 or a custom amount.
    const deckManaExceedsPool = currentStats != null && deckManaCost > currentStats.maxMana;
    // Win rate counts only decisive battles (win/loss) — draws don't affect it.
    const decisiveTotal = stats.wins + stats.losses;
    const winrate = decisiveTotal > 0 ? Math.round(stats.wins / decisiveTotal * 100) : 0;
    const elementalCards = this.allCards.filter(c => (c.cardType?.() ?? 'elemental') !== 'bonus');
    const bonusCards = this.allCards.filter(c => (c.cardType?.() ?? 'elemental') === 'bonus');
    const deckElementalIds = this.deckCardIds.filter(id => {
      const dc = this.allCards.find(c => parseInt(c.id()) === id);
      return dc && (dc.cardType?.() ?? 'elemental') !== 'bonus';
    });
    const deckBonusIds = this.deckCardIds.filter(id => {
      const dc = this.allCards.find(c => parseInt(c.id()) === id);
      return dc && (dc.cardType?.() ?? 'elemental') === 'bonus';
    });
    const filteredElemental = elementalCards.filter(c => {
      if (this.filterElementalElement !== 'all' && c.element() !== this.filterElementalElement) return false;
      if (this.filterElementalCost !== '' && String(c.cost()) !== this.filterElementalCost) return false;
      if (this.filterElementalSearch && !c.name().toLowerCase().includes(this.filterElementalSearch.toLowerCase())) return false;
      if (this.hideElementalAdded && deckSet.has(parseInt(c.id()))) return false;
      return true;
    });
    const filteredBonus = bonusCards.filter(c => {
      if (this.filterBonusCost !== '' && String(c.cost()) !== this.filterBonusCost) return false;
      if (this.filterBonusEffect !== 'all' && (c.bonusEffect?.() ?? '') !== this.filterBonusEffect) return false;
      if (this.filterBonusSearch && !c.name().toLowerCase().includes(this.filterBonusSearch.toLowerCase())) return false;
      if (this.hideBonusAdded && deckSet.has(parseInt(c.id()))) return false;
      return true;
    });
    return m("div", {
      className: "ArenaProfilePage"
    }, m("div", {
      className: "ArenaProfilePage-stats"
    }, m("div", {
      className: "ArenaStats"
    }, m("div", {
      className: "ArenaStats-item ArenaStats-item--wins"
    }, m("span", {
      className: "ArenaStats-value"
    }, stats.wins), m("span", {
      className: "ArenaStats-label"
    }, app.translator.trans('forumaker-arena.forum.profile.stats.wins', {
      n: stats.wins
    }))), m("div", {
      className: "ArenaStats-item ArenaStats-item--losses"
    }, m("span", {
      className: "ArenaStats-value"
    }, stats.losses), m("span", {
      className: "ArenaStats-label"
    }, app.translator.trans('forumaker-arena.forum.profile.stats.losses', {
      n: stats.losses
    }))), m("div", {
      className: "ArenaStats-item ArenaStats-item--draws"
    }, m("span", {
      className: "ArenaStats-value"
    }, stats.draws), m("span", {
      className: "ArenaStats-label"
    }, app.translator.trans('forumaker-arena.forum.profile.stats.draws', {
      n: stats.draws
    }))), m("div", {
      className: "ArenaStats-item ArenaStats-item--winrate"
    }, m("span", {
      className: "ArenaStats-value"
    }, winrate, "%"), m("span", {
      className: "ArenaStats-label"
    }, app.translator.trans('forumaker-arena.forum.profile.stats.winrate')))), currentStats && m("div", {
      className: "ArenaStats ArenaStats--vitals"
    }, m("div", {
      className: "ArenaStats-item ArenaStats-item--hp"
    }, m("span", {
      className: "ArenaStats-value"
    }, currentStats.maxHp), m("span", {
      className: "ArenaStats-label"
    }, app.translator.trans('forumaker-arena.lib.stats.hp'))), m("div", {
      className: "ArenaStats-item ArenaStats-item--mana"
    }, m("span", {
      className: "ArenaStats-value"
    }, currentStats.maxMana), m("span", {
      className: "ArenaStats-label"
    }, manaLabel))), m("div", {
      className: "ArenaProfilePage-statsIcons"
    }, questsEnabled() &&
    // Wrapper carries the mobile divider line above the button
    // (see ArenaProfilePage-statsIconCell) — matching the one
    // above HP/mana, which lives on .ArenaStats-item itself.
    // The button can't carry it directly: it already has its own
    // border on every side, and a border-top there would only
    // span the 44px button instead of the full grid column.
    m("div", {
      className: "ArenaProfilePage-statsIconCell"
    }, m(Button, {
      className: "Button Button--icon ArenaProfilePage-questsBtn",
      icon: "fas fa-tasks",
      title: app.translator.trans('forumaker-arena.forum.quests.btn'),
      "aria-label": app.translator.trans('forumaker-arena.forum.quests.btn'),
      onclick: () => app.modal.show(QuestsModal)
    })), m("div", {
      className: "ArenaProfilePage-statsIconCell"
    }, m(Button, {
      className: "Button Button--icon ArenaProfilePage-statsOverviewBtn",
      icon: "fas fa-chart-column",
      title: app.translator.trans('forumaker-arena.forum.profile.stats_overview.btn'),
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.stats_overview.btn'),
      onclick: () => app.modal.show(StatsOverviewModal)
    })))), m("div", {
      className: "ArenaProfilePage-battlePass"
    }, m(BattlePassBar, {
      games: stats.wins + stats.losses + stats.draws
    })), this.canManageOverrides() && m("div", {
      className: "ArenaProfilePage-section ArenaProfilePage-section--admin"
    }, m("div", {
      className: "ArenaProfilePage-adminHeader"
    }, m("h3", null, m("i", {
      className: "fas fa-user-cog"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.override.title'))), m("p", {
      className: "helpText"
    }, app.translator.trans('forumaker-arena.forum.profile.override.help')), m("div", {
      className: "ArenaProfilePage-overrideGrid"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: ARENA_ICONS.hp,
      style: {
        color: 'var(--arena-hp)'
      }
    }), " ", app.translator.trans('forumaker-arena.forum.profile.override.max_hp')), m("input", {
      className: "FormControl",
      type: "number",
      min: 1,
      max: 999,
      value: this.overrideHp,
      placeholder: String(currentStats?.maxHp ?? 15),
      oninput: e => {
        this.overrideHp = e.target.value;
      }
    })), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: ARENA_ICONS.mana,
      style: {
        color: 'var(--arena-mana)'
      }
    }), ' ', app.translator.trans('forumaker-arena.forum.profile.override.max_mana', {
      mana: manaLabel
    })), m("input", {
      className: "FormControl",
      type: "number",
      min: 1,
      max: 999,
      value: this.overrideMana,
      placeholder: String(currentStats?.maxMana ?? 20),
      oninput: e => {
        this.overrideMana = e.target.value;
      }
    })), m("div", {
      className: "Form-group ArenaProfilePage-overrideHistory"
    }, m("label", null, "\xA0"), m(Button, {
      className: "Button ArenaProfilePage-historyBtn",
      onclick: () => app.modal.show(OverrideHistoryModal, {
        userId: parseInt(user.id()),
        username: user.displayName()
      })
    }, m("i", {
      className: "fas fa-history"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.override.history_btn'))), m("div", {
      className: "Form-group ArenaProfilePage-overrideSave"
    }, m("label", null, "\xA0"), m(Button, {
      className: "Button Button--primary",
      loading: this.savingOverride,
      onclick: () => this.saveOverride()
    }, m("i", {
      className: "fas fa-save"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.override.save_btn'))))), canManage && m("div", {
      className: "ArenaProfilePage-deckEditor"
    }, m("div", {
      className: "ArenaProfilePage-section"
    }, m("div", {
      className: "ArenaProfilePage-sectionHeader",
      style: {
        '--arena-section-accent': 'var(--arena-mana)'
      }
    }, m("div", {
      className: "ArenaProfilePage-sectionIcon"
    }, m("i", {
      className: "fas fa-layer-group"
    })), m("div", {
      className: "ArenaProfilePage-sectionHeading"
    }, m("h3", null, app.translator.trans('forumaker-arena.forum.profile.deck.title')), m("p", {
      className: "ArenaProfilePage-sectionSubtitle"
    }, app.translator.trans('forumaker-arena.forum.profile.deck.subtitle')))), this.deckSlots.length > 0 ? m("div", {
      className: "ArenaProfilePage-deckSlots"
    }, m("div", {
      className: "ArenaProfilePage-deckSlotRow"
    }, isProfileOwner && this.renamingSlotId === this.selectedDeckSlotId ?
    // Renaming edits the same slot in place — this input
    // sits exactly where the <select> normally is, instead
    // of opening a second field further down the page.
    m("input", {
      className: "FormControl ArenaProfilePage-deckSlotInput ArenaProfilePage-deckSlotInput--editing",
      type: "text",
      maxLength: 100,
      value: this.renameSlotName,
      oninput: e => {
        this.renameSlotName = e.target.value;
      },
      onkeydown: e => {
        if (e.key === 'Enter') this.confirmRename();
        if (e.key === 'Escape') this.cancelRename();
      }
    }) :
    // Players kept missing that the deck name itself is the
    // switcher: the <select> inherits .FormControl and reads
    // as a plain text field. The wrapper only carries the
    // chevron that marks it as a dropdown (pointer-events:
    // none in forum.less) — the <select> still fills it and
    // handles every interaction, so native keyboard and
    // mobile pickers are untouched.
    m("div", {
      className: "ArenaProfilePage-deckSlotSelect"
    }, m("i", {
      className: "fas fa-chevron-down ArenaProfilePage-deckSlotSelect-caret"
    }), m("select", {
      className: "FormControl ArenaProfilePage-deckSlotInput",
      value: this.selectedDeckSlotId ?? '',
      disabled: this.loadingDeckCards || this.slotActionLoading,
      onchange: e => this.selectSlot(parseInt(e.target.value, 10))
    }, this.deckSlots.map(slot =>
    // `selected` set explicitly on the <option> itself,
    // not just `value` on the parent <select>: when a
    // brand-new slot is appended to deckSlots and
    // selected in the very same redraw (createSlot()),
    // the browser can finish creating the new <option>
    // node without picking up the <select>'s value —
    // it kept showing the previously selected deck
    // until some later, unrelated redraw forced the
    // <select> to resync. Setting `selected` directly
    // on the matching option applies regardless of
    // that timing.
    m("option", {
      key: slot.id,
      value: slot.id,
      selected: slot.id === this.selectedDeckSlotId
    }, slot.name)))), (() => {
      const selected = this.deckSlots.find(s => s.id === this.selectedDeckSlotId);
      if (!selected) return null;

      // Card count + mana cost merged into one pill (same
      // height as the pencil/trash controls next to it).
      // The over-limit distinction lives in the tooltip
      // text (mana_total_title vs. mana_total_over_title).
      const countPart = m("span", {
        className: "ArenaProfilePage-deckStatsBadge-part"
      }, app.translator.trans('forumaker-arena.forum.profile.deck.count_fraction', {
        value: selected.cardCount,
        max: 20
      }));
      if (this.loadingDeckCards) {
        return m("span", {
          className: "ArenaProfilePage-deckStatsBadge"
        }, countPart);
      }
      return m(Tooltip, {
        text: transPlain(deckManaExceedsPool ? 'forumaker-arena.forum.profile.deck.mana_total_over_title' : 'forumaker-arena.forum.profile.deck.mana_total_title', {
          excess: deckManaCost - (currentStats?.maxMana ?? 0)
        }),
        position: "bottom"
      }, m("span", {
        className: "ArenaProfilePage-deckStatsBadge"
      }, countPart, m("span", {
        className: "ArenaProfilePage-deckStatsBadge-divider"
      }), m("span", {
        className: "ArenaProfilePage-deckStatsBadge-part"
      }, app.translator.trans('forumaker-arena.forum.profile.deck.mana_total', {
        mana: deckManaCost
      }))));
    })(), isProfileOwner && this.selectedDeckSlotId != null && (this.renamingSlotId === this.selectedDeckSlotId ? m('[', null, m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-check",
      title: app.translator.trans('forumaker-arena.forum.profile.deck.save_rename'),
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.save_rename'),
      loading: this.slotActionLoading,
      onclick: () => this.confirmRename()
    }), m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-times",
      title: app.translator.trans('forumaker-arena.forum.profile.deck.cancel'),
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.cancel'),
      disabled: this.slotActionLoading,
      onclick: () => this.cancelRename()
    })) : m('[', null, m(Button, {
      className: "Button Button--icon",
      icon: "fas fa-pen",
      title: app.translator.trans('forumaker-arena.forum.profile.deck.rename_slot'),
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.rename_slot'),
      disabled: this.slotActionLoading,
      onclick: () => {
        const slot = this.deckSlots.find(s => s.id === this.selectedDeckSlotId);
        if (slot) this.startRename(slot);
      }
    }), this.deckSlots.length > 1 && m(Button, {
      className: "Button Button--icon Button--danger",
      icon: "fas fa-trash",
      title: app.translator.trans('forumaker-arena.forum.profile.deck.delete_slot'),
      "aria-label": app.translator.trans('forumaker-arena.forum.profile.deck.delete_slot'),
      disabled: this.slotActionLoading,
      onclick: () => {
        const slot = this.deckSlots.find(s => s.id === this.selectedDeckSlotId);
        if (slot) this.deleteSlot(slot);
      }
    })))), isProfileOwner && (this.showNewSlotInput ? m("div", {
      className: "ArenaProfilePage-deckSlotNew"
    }, m("input", {
      className: "FormControl",
      type: "text",
      maxLength: 100,
      placeholder: app.translator.trans('forumaker-arena.forum.profile.deck.new_slot_placeholder'),
      value: this.newSlotName,
      oninput: e => {
        this.newSlotName = e.target.value;
      },
      onkeydown: e => {
        if (e.key === 'Enter') this.createSlot();
      }
    }), m(Button, {
      className: "Button Button--primary",
      loading: this.slotActionLoading,
      onclick: () => this.createSlot()
    }, app.translator.trans('forumaker-arena.forum.profile.deck.create_slot_confirm')), m(Button, {
      className: "Button",
      onclick: () => {
        this.showNewSlotInput = false;
        this.newSlotName = '';
      }
    }, app.translator.trans('forumaker-arena.forum.profile.deck.cancel'))) : this.deckSlots.length < MAX_DECK_SLOTS && m(Button, {
      className: "Button ArenaProfilePage-addSlotBtn",
      onclick: () => {
        this.showNewSlotInput = true;
      }
    }, m("i", {
      className: "fas fa-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.deck.create_slot')))) : m("div", {
      className: "ArenaProfilePage-emptyDeck"
    }, app.translator.trans('forumaker-arena.forum.profile.deck.no_slots')), slotLocked && m("div", {
      className: "ArenaProfilePage-battleLock"
    }, m("i", {
      className: "fas fa-lock"
    }), " ", app.translator.trans('forumaker-arena.forum.profile.battle_lock')), m("div", {
      className: "ArenaProfilePage-deckSplit"
    }, m("div", {
      className: "ArenaProfilePage-deckHalf"
    }, m("div", {
      className: "ArenaProfilePage-deckHalfLabel"
    }, m("i", {
      className: "fas fa-clone"
    }), app.translator.trans('forumaker-arena.forum.profile.deck.elemental_half'), m("span", {
      className: `ArenaProfilePage-halfCount ${deckElementalIds.length === 10 ? 'is-full' : ''}`
    }, app.translator.trans('forumaker-arena.forum.profile.deck.count_fraction', {
      value: deckElementalIds.length,
      max: 10
    }))), deckElementalIds.length === 0 ? m("div", {
      className: "ArenaProfilePage-emptyDeck"
    }, app.translator.trans('forumaker-arena.forum.profile.deck.add_elemental')) : this.renderDeckRow(deckElementalIds, deckDisabled, cardBackImage, 'elemental')), m("div", {
      className: "ArenaProfilePage-deckHalf"
    }, m("div", {
      className: "ArenaProfilePage-deckHalfLabel ArenaProfilePage-deckHalfLabel--bonus"
    }, m("i", {
      className: "fas fa-star"
    }), app.translator.trans('forumaker-arena.forum.profile.deck.bonus_half'), m("span", {
      className: `ArenaProfilePage-halfCount ${deckBonusIds.length === 10 ? 'is-full' : ''}`
    }, app.translator.trans('forumaker-arena.forum.profile.deck.count_fraction', {
      value: deckBonusIds.length,
      max: 10
    }))), deckBonusIds.length === 0 ? m("div", {
      className: "ArenaProfilePage-emptyDeck"
    }, app.translator.trans('forumaker-arena.forum.profile.deck.add_bonus')) : this.renderDeckRow(deckBonusIds, deckDisabled, cardBackImage, 'bonus')))), m("div", {
      className: "ArenaProfilePage-section"
    }, m("div", {
      className: "ArenaProfilePage-sectionHeader",
      style: {
        '--arena-section-accent': '#80b77e'
      }
    }, m("div", {
      className: "ArenaProfilePage-sectionIcon"
    }, m("i", {
      className: "fas fa-clone"
    })), m("div", {
      className: "ArenaProfilePage-sectionHeading"
    }, m("h3", null, app.translator.trans('forumaker-arena.forum.profile.sections.elemental')), m("p", {
      className: "ArenaProfilePage-sectionSubtitle"
    }, app.translator.trans('forumaker-arena.forum.profile.sections.elemental_subtitle', {
      link: m("a", {
        onclick: () => app.modal.show(ElementsGuideModal)
      }, app.translator.trans('forumaker-arena.forum.profile.sections.elemental_subtitle_link'))
    })))), m("div", {
      className: "ArenaProfilePage-filters"
    }, m("input", {
      className: "FormControl ArenaProfilePage-searchInput",
      type: "search",
      placeholder: searchLabel,
      value: this.filterElementalSearch,
      oninput: e => {
        this.filterElementalSearch = e.target.value;
      }
    }), m("select", {
      className: "FormControl",
      value: this.filterElementalElement,
      onchange: e => {
        this.filterElementalElement = e.target.value;
      }
    }, m("option", {
      value: "all"
    }, app.translator.trans('forumaker-arena.forum.profile.filter.all_elements')), elementKeys().map(el => m("option", {
      key: el,
      value: el
    }, elementLabel(el)))), m("input", {
      className: "FormControl ArenaProfilePage-manaInput",
      type: "number",
      min: "0",
      placeholder: manaLabel,
      value: this.filterElementalCost,
      oninput: e => {
        this.filterElementalCost = e.target.value;
      }
    }), m(Button, {
      className: `Button ArenaProfilePage-hideAdded ${this.hideElementalAdded ? 'active' : ''}`,
      onclick: () => {
        this.hideElementalAdded = !this.hideElementalAdded;
      },
      title: app.translator.trans('forumaker-arena.forum.profile.filter.hide_added')
    }, m("i", {
      className: `fas ${this.hideElementalAdded ? 'fa-eye' : 'fa-eye-slash'}`
    }), ' ', this.hideElementalAdded ? app.translator.trans('forumaker-arena.forum.profile.filter.show_all') : app.translator.trans('forumaker-arena.forum.profile.filter.hide_added'))), m("div", {
      className: "ArenaCardGrid"
    }, filteredElemental.map(card => {
      const inDeck = deckSet.has(parseInt(card.id()));
      const sectionFull = deckElementalIds.length >= 10;
      return m(CardComponent, {
        key: card.id(),
        card: card,
        selected: inDeck,
        disabled: deckDisabled || !inDeck && sectionFull,
        cardBackImage: cardBackImage,
        onclick: () => this.toggleCard(card)
      });
    }))), m("div", {
      className: "ArenaProfilePage-section"
    }, m("div", {
      className: "ArenaProfilePage-sectionHeader",
      style: {
        '--arena-section-accent': 'var(--arena-bonus)'
      }
    }, m("div", {
      className: "ArenaProfilePage-sectionIcon"
    }, m("i", {
      className: "fas fa-star"
    })), m("div", {
      className: "ArenaProfilePage-sectionHeading"
    }, m("h3", null, app.translator.trans('forumaker-arena.forum.profile.sections.bonus')), m("p", {
      className: "ArenaProfilePage-sectionSubtitle"
    }, app.translator.trans('forumaker-arena.forum.profile.sections.bonus_subtitle')))), m("div", {
      className: "ArenaProfilePage-filters"
    }, m("input", {
      className: "FormControl ArenaProfilePage-searchInput",
      type: "search",
      placeholder: searchLabel,
      value: this.filterBonusSearch,
      oninput: e => {
        this.filterBonusSearch = e.target.value;
      }
    }), m("select", {
      className: "FormControl",
      value: this.filterBonusEffect,
      onchange: e => {
        this.filterBonusEffect = e.target.value;
      }
    }, m("option", {
      value: "all"
    }, app.translator.trans('forumaker-arena.forum.profile.filter.all_effects')), Object.keys(BONUS_EFFECT_LABELS).map(key => m("option", {
      key: key,
      value: key
    }, app.translator.trans(`forumaker-arena.lib.bonus_effects.${key}`)))), m("input", {
      className: "FormControl ArenaProfilePage-manaInput",
      type: "number",
      min: "0",
      placeholder: manaLabel,
      value: this.filterBonusCost,
      oninput: e => {
        this.filterBonusCost = e.target.value;
      }
    }), m(Button, {
      className: `Button ArenaProfilePage-hideAdded ${this.hideBonusAdded ? 'active' : ''}`,
      onclick: () => {
        this.hideBonusAdded = !this.hideBonusAdded;
      },
      title: app.translator.trans('forumaker-arena.forum.profile.filter.hide_added')
    }, m("i", {
      className: `fas ${this.hideBonusAdded ? 'fa-eye' : 'fa-eye-slash'}`
    }), ' ', this.hideBonusAdded ? app.translator.trans('forumaker-arena.forum.profile.filter.show_all') : app.translator.trans('forumaker-arena.forum.profile.filter.hide_added'))), m("div", {
      className: "ArenaCardGrid"
    }, filteredBonus.map(card => {
      const inDeck = deckSet.has(parseInt(card.id()));
      const sectionFull = deckBonusIds.length >= 10;
      return m(CardComponent, {
        key: card.id(),
        card: card,
        selected: inDeck,
        disabled: deckDisabled || !inDeck && sectionFull,
        cardBackImage: cardBackImage,
        onclick: () => this.toggleCard(card)
      });
    }))), this.saveStatus !== 'idle' && m("div", {
      className: "ArenaProfilePage-deckSaveBar"
    }, this.saveStatus === 'saving' && m(Alert, {
      dismissible: false,
      className: "ArenaProfilePage-autosaveAlert"
    }, m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }), ' ', app.translator.trans('forumaker-arena.forum.profile.deck.saving')), this.saveStatus === 'saved' && m(Alert, {
      type: "success",
      className: "ArenaProfilePage-autosaveAlert",
      ondismiss: () => this.dismissAutosaveStatus()
    }, app.translator.trans('forumaker-arena.forum.profile.deck.saved')), this.saveStatus === 'error' && m(Alert, {
      type: "error",
      className: "ArenaProfilePage-autosaveAlert",
      ondismiss: () => this.dismissAutosaveStatus(),
      controls: [m(Button, {
        className: "Button Button--link",
        onclick: () => this.flushAutosave()
      }, app.translator.trans('forumaker-arena.forum.profile.deck.retry_btn'))]
    }, app.translator.trans('forumaker-arena.forum.profile.deck.error')))), isProfileOwner && m("div", {
      className: "ArenaProfilePage-section ArenaProfilePage-cardBacks"
    }, m("div", {
      className: "ArenaProfilePage-sectionHeader",
      style: {
        '--arena-section-accent': '#14b8a6'
      }
    }, m("div", {
      className: "ArenaProfilePage-sectionIcon"
    }, m("i", {
      className: "fas fa-clone"
    })), m("div", {
      className: "ArenaProfilePage-sectionHeading"
    }, m("h3", null, app.translator.trans('forumaker-arena.forum.profile.card_backs.title')), m("p", {
      className: "ArenaProfilePage-sectionSubtitle"
    }, app.translator.trans('forumaker-arena.forum.profile.card_backs.subtitle')))), m("div", {
      className: "ArenaCardBackGrid"
    }, (() => {
      // "Личная" (isPublic: false) backs no longer ride along in
      // the public arenaCardBacks payload at all (see
      // FilterActiveCardBacks on the backend) — a visitor's
      // browser never receives another player's exclusive design,
      // not just a client-side-hidden one. This grid only ever
      // renders for isProfileOwner (see the wrapping condition
      // above), so it's safe to add the owner's own granted
      // private backs back in here from arenaGrantedCardBacks — a
      // full-object, owner-only field (see UserResourceFields).
      const publicIds = new Set(ARENA_CARD_BACKS.map(b => b.id));
      const grantedBacks = (user.attribute('arenaGrantedCardBacks') ?? []).filter(b => !publicIds.has(b.id)).map(b => ({
        ...b,
        threshold: 0,
        isPublic: false
      }));
      return [{
        id: 0,
        imageUrl: null,
        threshold: 0,
        isPublic: true
      }, ...ARENA_CARD_BACKS, ...grantedBacks];
    })().map(back => {
      const selectedBack = user.attribute('arenaCardBack') ?? 0;
      const selected = selectedBack === back.id;
      // Granted private backs are always unlocked — unlike public
      // backs, they're never gated by the win threshold.
      const locked = back.id !== 0 && back.isPublic !== false && stats.wins < back.threshold;
      return m("div", {
        key: back.id,
        className: ['ArenaCardBackOption', selected ? 'ArenaCardBackOption--selected' : '', locked ? 'ArenaCardBackOption--locked' : ''].filter(Boolean).join(' '),
        onclick: !locked ? () => this.selectCardBack(back.id) : undefined
      }, m("div", {
        className: "ArenaCardBackOption-preview"
      }, back.imageUrl ? m("img", {
        src: back.imageUrl,
        alt: ""
      }) : m("i", {
        className: "fas fa-ban"
      }), locked && m("div", {
        className: "ArenaCardBackOption-lockOverlay"
      }, m("i", {
        className: "fas fa-lock"
      }))), m("div", {
        className: "ArenaCardBackOption-label"
      }, back.id === 0 ? app.translator.trans('forumaker-arena.forum.profile.card_backs.none') : back.name || app.translator.trans('forumaker-arena.forum.profile.card_backs.slot', {
        n: back.id
      })), locked && m("div", {
        className: "ArenaCardBackOption-requirement"
      }, app.translator.trans('forumaker-arena.forum.profile.card_backs.requires_wins', {
        n: back.threshold
      })));
    }), !!app.forum.attribute('arenaShowCustomCardBack') && (() => {
      const pending = !!user.attribute('arenaHasPendingCustomCardBackRequest');
      return m("div", {
        className: `ArenaCardBackOption ArenaCardBackOption--custom${pending ? ' ArenaCardBackOption--pending' : ''}`,
        onclick: () => this.openCustomCardBackRequest()
      }, m("div", {
        className: "ArenaCardBackOption-preview"
      }, m("i", {
        className: "fas fa-palette"
      })), m("div", {
        className: "ArenaCardBackOption-label"
      }, app.translator.trans('forumaker-arena.forum.profile.card_backs.custom.label')), pending && m("div", {
        className: "ArenaCardBackOption-requirement"
      }, app.translator.trans('forumaker-arena.forum.profile.card_backs.custom.pending')));
    })())));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/ArenaProfilePage', ArenaProfilePage);